import { useState, useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import { format } from "date-fns";
import { getInvoiceById, getUser } from "@/service/weexpertService";
import { ROLES } from "@/constants";

const vatMap = {
  '23%': 23,
  '8%': 8,
  '5%': 5,
  '0%': 0,
  'zw.': 0,
  'np.': 0,
  'np.EU': 0
};

// Normalize VAT type to match dropdown options
// Handles cases where API returns "np" instead of "np." etc.
const normalizeVatType = (vatType) => {
  if (!vatType) return '23%';
  // If already valid, return as-is
  if (vatMap.hasOwnProperty(vatType)) return vatType;
  // Handle "np" without period
  if (vatType === 'np') return 'np.';
  // Handle "zw" without period
  if (vatType === 'zw') return 'zw.';
  // Default fallback
  return vatType;
};

const calculateVatAmount = (priceNetto, amount, vatRate) => {
  const rate = vatMap[vatRate] || 0;
  return (priceNetto / 100) * rate * amount;
};

export const useInvoiceCorrectionForm = (invoiceId) => {
  // User query
  const { data: userData, isLoading: isUserLoading } = useQuery({
    queryKey: 'userMe',
    queryFn: getUser,
    retry: false
  });
  const user = userData?.data;

  // Original invoice query
  const { data: invoiceData, isLoading: isInvoiceLoading } = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => getInvoiceById(invoiceId),
    enabled: !!invoiceId,
  });
  const originalInvoice = invoiceData?.data;

  // Correction form state
  const [correctionReason, setCorrectionReason] = useState("");
  const [issueDate, setIssueDate] = useState(new Date());
  const [correctedItems, setCorrectedItems] = useState([]);
  const [counterpartyCorrection, setCounterpartyCorrection] = useState(null);

  // Initialize corrected items from original invoice
  useEffect(() => {
    if (originalInvoice?.invoiceItems) {
      const items = originalInvoice.invoiceItems.map((item) => {
        const normalizedVatType = normalizeVatType(item.typeOfVat);
        return {
        id: item.id,
        // Before descriptive fields (read-only)
        beforeName: item.name,
        beforeGTU: item.GTU,
        beforePkwiu: item.pkwiu,
        beforeUnit: item.unit,
        beforeTypeOfVat: normalizedVatType,
        // After descriptive fields (editable)
        afterName: item.name,
        afterGTU: item.GTU,
        afterPkwiu: item.pkwiu,
        afterUnit: item.unit,
        afterTypeOfVat: normalizedVatType,
        // Before numeric values (read-only)
        beforeAmount: item.amount,
        beforePriceNetto: item.priceNetto,
        beforeVat: item.vat,
        beforePriceBrutto: item.priceBrutto,
        // After numeric values (editable)
        afterAmount: item.amount,
        afterPriceNetto: item.priceNetto,
        afterVat: item.vat,
        afterPriceBrutto: item.priceBrutto,
        // Diff values (calculated)
        diffAmount: 0,
        diffPriceNetto: 0,
        diffVat: 0,
        diffPriceBrutto: 0,
        // Flag for new items added in correction
        isNew: false,
        };
      });
      setCorrectedItems(items);
    }
  }, [originalInvoice]);

  // Handle item change
  const handleItemChange = (index, field, value) => {
    setCorrectedItems(prevItems => {
      const newItems = [...prevItems];
      const item = { ...newItems[index] };

      if (field === 'afterAmount') {
        item.afterAmount = parseFloat(value) || 0;
      } else if (field === 'afterPriceNetto') {
        item.afterPriceNetto = parseFloat(value) || 0;
      } else if (field === 'afterTypeOfVat') {
        item.afterTypeOfVat = value;
      } else if (field === 'afterName') {
        item.afterName = value;
      } else if (field === 'afterUnit') {
        item.afterUnit = value;
      } else if (field === 'afterGTU') {
        item.afterGTU = value;
      } else if (field === 'afterPkwiu') {
        item.afterPkwiu = value;
      }

      // Clear PKWIU when VAT type changes to non-reduced rate
      if (field === 'afterTypeOfVat') {
        if (!['8%', '5%', '0%'].includes(value)) {
          item.afterPkwiu = '';
        }
      }

      // Recalculate after values
      const totalNetto = item.afterPriceNetto * item.afterAmount;
      item.afterVat = calculateVatAmount(item.afterPriceNetto, item.afterAmount, item.afterTypeOfVat);
      item.afterPriceBrutto = totalNetto + item.afterVat;

      // Calculate diffs
      const beforeTotalNetto = item.beforePriceNetto * item.beforeAmount;
      const beforeVat = item.beforeVat * item.beforeAmount;
      const beforeBrutto = item.beforePriceBrutto * item.beforeAmount;

      item.diffAmount = item.afterAmount - item.beforeAmount;
      item.diffPriceNetto = totalNetto - beforeTotalNetto;
      item.diffVat = item.afterVat - beforeVat;
      item.diffPriceBrutto = item.afterPriceBrutto - beforeBrutto;

      newItems[index] = item;
      return newItems;
    });
  };

  // Add new item
  const addNewItem = () => {
    setCorrectedItems(prevItems => [
      ...prevItems,
      {
        id: null,
        // Before descriptive fields (empty for new items)
        beforeName: "",
        beforeGTU: "-",
        beforePkwiu: "",
        beforeUnit: "szt.",
        beforeTypeOfVat: "23%",
        // After descriptive fields (editable)
        afterName: "",
        afterGTU: "-",
        afterPkwiu: "",
        afterUnit: "szt.",
        afterTypeOfVat: "23%",
        // Before numeric values (zero for new items)
        beforeAmount: 0,
        beforePriceNetto: 0,
        beforeVat: 0,
        beforePriceBrutto: 0,
        // After numeric values (editable)
        afterAmount: 1,
        afterPriceNetto: 0,
        afterVat: 0,
        afterPriceBrutto: 0,
        // Diff values
        diffAmount: 1,
        diffPriceNetto: 0,
        diffVat: 0,
        diffPriceBrutto: 0,
        isNew: true,
      },
    ]);
  };

  // Remove item
  const removeItem = (index) => {
    setCorrectedItems(prevItems => prevItems.filter((_, i) => i !== index));
  };

  // Calculate totals
  const totals = useMemo(() => {
    const result = {
      beforeNetto: 0,
      beforeVat: 0,
      beforeBrutto: 0,
      afterNetto: 0,
      afterVat: 0,
      afterBrutto: 0,
      diffNetto: 0,
      diffVat: 0,
      diffBrutto: 0,
    };

    correctedItems.forEach(item => {
      result.beforeNetto += item.beforePriceNetto * item.beforeAmount;
      result.beforeVat += item.beforeVat * item.beforeAmount;
      result.beforeBrutto += item.beforePriceBrutto * item.beforeAmount;
      result.afterNetto += item.afterPriceNetto * item.afterAmount;
      result.afterVat += item.afterVat;
      result.afterBrutto += item.afterPriceBrutto;
    });

    result.diffNetto = result.afterNetto - result.beforeNetto;
    result.diffVat = result.afterVat - result.beforeVat;
    result.diffBrutto = result.afterBrutto - result.beforeBrutto;

    return result;
  }, [correctedItems]);

  // Validation
  const validate = () => {
    const errors = [];

    if (!correctionReason || correctionReason.length < 10) {
      errors.push('correctionReason');
    }

    if (!issueDate) {
      errors.push('issueDate');
    }

    // Check if at least one item has changes
    const hasChanges = correctedItems.some(item =>
      item.diffAmount !== 0 ||
      item.diffPriceNetto !== 0 ||
      item.isNew
    );

    if (!hasChanges) {
      errors.push('noChanges');
    }

    return errors;
  };

  // Get submit data - matches API contract from CORRECTION_INVOICE_API.md
  const getSubmitData = () => {
    const data = {
      correctionReason,
      issueDate: format(issueDate, 'yyyy-MM-dd'),
      correctedItems: correctedItems.map(item => ({
        originalItemId: item.isNew ? undefined : item.id,
        afterName: item.afterName,
        afterUnit: item.afterUnit,
        afterTypeOfVat: item.afterTypeOfVat,
        afterGTU: item.afterGTU || undefined,
        afterPkwiu: item.afterPkwiu || undefined,
        afterAmount: item.afterAmount,
        afterPriceNetto: item.afterPriceNetto,
        afterPriceBrutto: item.afterPriceBrutto,
        afterVat: item.afterVat,
        diffAmount: item.diffAmount,
        diffNetto: item.diffPriceNetto,
        diffBrutto: item.diffPriceBrutto,
        diffVat: item.diffVat,
      })),
    };

    if (counterpartyCorrection) {
      data.counterpartyCorrection = counterpartyCorrection;
    }

    return data;
  };

  // Permissions
  const isAdmin = [ROLES.ADMIN, ROLES.HEAD_OF_DEPARTMENT, ROLES.SUPER_ADMIN].includes(user?.roles);

  return {
    // Loading state
    isLoading: isUserLoading || isInvoiceLoading,

    // User
    user,
    isAdmin,

    // Original invoice
    originalInvoice,

    // Correction form state
    correctionReason,
    setCorrectionReason,
    issueDate,
    setIssueDate,
    correctedItems,
    setCorrectedItems,
    counterpartyCorrection,
    setCounterpartyCorrection,

    // Item handlers
    handleItemChange,
    addNewItem,
    removeItem,

    // Totals
    totals,

    // Methods
    validate,
    getSubmitData,
  };
};
